/**
 * Detail page for a single BridgeClubOrder.
 *
 * Three actions:
 *   - Notify Customer Ready (sends email, flips status to ready_for_pickup)
 *   - Load in POS (creates a pre-staged POSTransaction so the cashier
 *     can complete it at the till; auto-attaches the BC member so
 *     points fire on completion). Pay-in-store orders only.
 *   - Print Receipt (paid-online orders that never went through the
 *     till). Opens a print-friendly view via window.print().
 *
 * Wired into the store portal at /store/orders/:orderNumber/.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface OrderItem {
  id?: number;
  name: string;
  quantity: number | string;
  unit_price?: number;
  line_total: number | string;
}

interface OrderDetail {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  status: string;
  payment_status: string;
  pickup_method: string;
  total: number;
  items: OrderItem[];
  items_summary?: string;
  created_at: string | null;
  paid_at: string | null;
}

interface ReceiptData {
  order_number: string;
  paid_at: string | null;
  customer_name: string;
  items: OrderItem[];
  subtotal: number;
  convenience_fee: number;
  tax: number;
  total: number;
  helcim_transaction_id: string;
  card_last_four: string;
  card_type: string;
}

const BridgeClubOrderDetail: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notifiedAt, setNotifiedAt] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ orders: OrderDetail[] }>(
        '/store/pickup-orders/',
      );
      const match = res.data.orders.find(
        (o) => o.order_number === orderNumber,
      );
      if (!match) {
        setError(`Order ${orderNumber} not found in pickup queue.`);
        setOrder(null);
      } else {
        setOrder(match);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const isPayInStore = order?.payment_status === 'pay_in_store';
  const isPaidOnline = order?.payment_status === 'paid';

  const notifyReady = async () => {
    if (!order) return;
    setBusy('notify');
    try {
      const res = await api.post<{
        notified_ready_at: string;
        status: string;
      }>(`/store/orders/${order.id}/notify-ready/`);
      setNotifiedAt(res.data.notified_ready_at);
      setOrder({ ...order, status: res.data.status });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Notify failed';
      setError(msg);
    } finally {
      setBusy(null);
    }
  };

  const loadInPos = async () => {
    if (!order) return;
    setBusy('load');
    try {
      const res = await api.post<{
        transaction_number: string;
        redirect_url: string;
      }>(`/store/orders/${order.id}/load-in-pos/`);
      navigate(res.data.redirect_url);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const detail = err.response?.data?.message || err.response?.data?.error;
      setError(detail || 'Load in POS failed');
    } finally {
      setBusy(null);
    }
  };

  const openReceipt = async () => {
    if (!order) return;
    setBusy('receipt');
    try {
      const res = await api.get<ReceiptData>(
        `/store/orders/${order.id}/receipt/`,
      );
      setReceipt(res.data);
      setTimeout(() => window.print(), 100);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Receipt unavailable';
      setError(msg);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (error && !order) {
    return <div style={{ padding: 24, color: '#dc2626' }}>Error: {error}</div>;
  }
  if (!order) return null;

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      {/* Printable receipt overlay */}
      {receipt && (
        <PrintableReceipt receipt={receipt} onClose={() => setReceipt(null)} />
      )}

      <div className="no-print">
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>
          Order #{order.order_number}
        </h1>
        <div style={{ color: '#6b7280', marginBottom: 24 }}>
          {order.customer_name} · {order.customer_phone || 'no phone'} ·{' '}
          {order.customer_email || 'no email'}
        </div>

        {error && (
          <div
            style={{
              padding: 12,
              background: '#fee2e2',
              color: '#991b1b',
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 20,
            marginBottom: 20,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <strong>Status:</strong> {order.status}
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong>Payment:</strong> {order.payment_status}
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong>Pickup:</strong> {order.pickup_method}
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong>Total:</strong> ${order.total.toFixed(2)}
          </div>
          <h3 style={{ marginTop: 16, fontSize: 16 }}>Items</h3>
          <ul>
            {order.items.map((i, idx) => (
              <li key={idx}>
                {i.quantity}x {i.name} — ${
                  typeof i.line_total === 'number'
                    ? i.line_total.toFixed(2)
                    : i.line_total
                }
              </li>
            ))}
          </ul>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={notifyReady}
            disabled={busy !== null || !!notifiedAt}
            style={{
              background: notifiedAt ? '#9ca3af' : '#059669',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: 6,
              fontSize: 15,
              fontWeight: 600,
              minHeight: 48,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {notifiedAt
              ? `Customer Notified ${new Date(notifiedAt).toLocaleTimeString()}`
              : 'Notify Customer Ready for Pickup'}
          </button>

          {isPayInStore && (
            <button
              onClick={loadInPos}
              disabled={busy !== null}
              style={{
                background: '#2563eb',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 6,
                fontSize: 15,
                fontWeight: 600,
                minHeight: 48,
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              Load in POS
            </button>
          )}

          {isPaidOnline && (
            <button
              onClick={openReceipt}
              disabled={busy !== null}
              style={{
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                padding: '12px 20px',
                borderRadius: 6,
                fontSize: 15,
                fontWeight: 600,
                minHeight: 48,
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              Print Receipt
            </button>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};

const PrintableReceipt: React.FC<{
  receipt: ReceiptData;
  onClose: () => void;
}> = ({ receipt, onClose }) => (
  <div
    className="print-only"
    style={{
      maxWidth: 360,
      margin: '0 auto',
      fontFamily: 'monospace',
      fontSize: 13,
      padding: 12,
    }}
  >
    <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 16 }}>
      Red Bridge Farm Feed Co.
    </div>
    <div style={{ textAlign: 'center', marginBottom: 12, fontSize: 11 }}>
      ONLINE ORDER
    </div>
    <div>Order #{receipt.order_number}</div>
    <div>
      Date:{' '}
      {receipt.paid_at ? new Date(receipt.paid_at).toLocaleString() : '—'}
    </div>
    <div>Customer: {receipt.customer_name}</div>
    <hr />
    <table style={{ width: '100%', fontSize: 12 }}>
      <tbody>
        {receipt.items.map((i, idx) => (
          <tr key={idx}>
            <td>
              {i.quantity}x {i.name}
            </td>
            <td style={{ textAlign: 'right' }}>
              ${typeof i.line_total === 'number'
                ? i.line_total.toFixed(2)
                : i.line_total}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <hr />
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span>Subtotal</span>
      <span>${receipt.subtotal.toFixed(2)}</span>
    </div>
    {receipt.convenience_fee > 0 && (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Convenience fee</span>
        <span>${receipt.convenience_fee.toFixed(2)}</span>
      </div>
    )}
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span>Tax</span>
      <span>${receipt.tax.toFixed(2)}</span>
    </div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontWeight: 700,
        marginTop: 4,
      }}
    >
      <span>Total</span>
      <span>${receipt.total.toFixed(2)}</span>
    </div>
    <hr />
    <div style={{ fontSize: 11 }}>
      Payment: Helcim {receipt.card_type || 'Card'} ****
      {receipt.card_last_four || '????'}
      <br />
      Txn: {receipt.helcim_transaction_id || '—'}
    </div>
    {/* The "points earned" line was removed with the backend's points_earned
        field (2026-08-29): a paid-online BC order accrues no points, so the
        number printed here was never real. See store_order_receipt. */}
    <button
      className="no-print"
      onClick={onClose}
      style={{
        marginTop: 24,
        background: 'transparent',
        border: '1px solid #d1d5db',
        padding: '6px 14px',
        cursor: 'pointer',
      }}
    >
      Close
    </button>
  </div>
);

export default BridgeClubOrderDetail;
